<script setup lang="ts">
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { FavoriteButton } from '@haskoning/vue-component-library'

import { useScenarioStore } from '@/stores/scenario'
import type { Scenario } from '@/stores/type/scenario.type'
import { useProjectStore } from '@/stores/project'
import { useMapStore } from '@/stores/map'
import DropdownMenu from '@/components/form/DropdownMenu.vue'
import ViewingOptions from '@/components/form/ViewingOptions.vue'
import FilterPanel from '@/components/scenario-result/filter/FilterPanel.vue'

const props = defineProps({
  mapId: {
    type: Number,
    required: false
  }
})

const emit = defineEmits([
  'apply-filter',
  'clear-all-filter',
  'update-selected-calculations',
  'update-viewing-option'
])

const handleSelectedCalculationsUpdate = (selectedCalculations: number[]) => {
  emit('update-selected-calculations', selectedCalculations)
}

const showActionBar = ref(true)

const router = useRouter()

const scenarioStore = useScenarioStore()

const projectStore = useProjectStore()

const mapStore = useMapStore()
const { exportLoading } = storeToRefs(mapStore)

const { currentItem } = storeToRefs(scenarioStore)

const formatDate = (dateString?: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const toggleFavorite = async () => {
  if (currentItem.value) {
    currentItem.value.favorite = !currentItem.value.favorite
    const updateItem = {
      id: currentItem.value.id,
      favorite: currentItem.value.favorite
    } as Scenario
    await scenarioStore.update(updateItem)
  }
}

const navigateToProject = () => {
  router.push({ name: 'project-detail' })
}

const navigateToInput = async () => {
  if (currentItem.value) {
    const projectId = currentItem.value.project
    await projectStore.fetchItem(projectId)
    router.push({
      name: 'scenario-form',
      params: {
        id: projectId,
        scenarioId: currentItem.value.id
      }
    })
  }
}

const handleViewingOptionUpdate = (newOption: string) => {
  emit('update-viewing-option', newOption)
}
const baseMapId = computed(() => props.mapId)
const exportShapeFile = async () => {
  if (currentItem.value?.id && typeof baseMapId.value === 'number') {
    await mapStore.downloadShapeFile(baseMapId.value, 'shp')
  } else {
    console.warn('No current scenario ID or map ID available for exporting shape file.')
  }
}
const exportCSVFile = async () => {
  if (currentItem.value?.id && typeof baseMapId.value === 'number') {
    await mapStore.downloadShapeFile(baseMapId.value, 'csv')
  } else {
    console.warn('No current scenario ID or map ID available for exporting CSV file.')
  }
}
</script>

<template>
  <v-card class="mx-2 py-4" elevation="0" flat>
    <div class="d-flex align-center" style="width: 100%; position: relative">
      <div style="width: 200px"></div>
      <div class="flex-grow-1 d-flex align-center justify-center">
        <v-card-title class="header-font d-flex align-center text-center ml-10">
          <span>
            Simulation results for {{ currentItem?.name }} -
            {{ formatDate(currentItem?.created) }}
          </span>
          <FavoriteButton :item="currentItem ?? {}" @update-favorite="toggleFavorite" />
        </v-card-title>
      </div>
      <div style="width: 200px; display: flex; justify-content: flex-end; align-items: center">
        <v-btn
          variant="text"
          color="primary"
          class="pa-0 hide-action-bar-btn"
          style="min-width: 160px; text-transform: none; padding-left: 12px; padding-right: 12px"
          @click="showActionBar = true"
          v-if="!showActionBar"
        >
          <v-icon start size="18">mdi-eye-outline</v-icon>
          Show Action Bar
        </v-btn>
      </div>
    </div>
  </v-card>

  <v-dialog
    v-model="exportLoading"
    persistent
    width="320"
    transition="dialog-bottom-transition"
    :scrim="true"
  >
    <v-card
      class="d-flex flex-column align-center justify-center"
      style="padding: 32px 24px; background: #fff; border-radius: 12px"
      elevation="2"
    >
      <v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
      <span style="font-size: 1rem; font-weight: 500">Exporting data...</span>
    </v-card>
  </v-dialog>

  <v-card
    class="d-flex flex-wrap align-center justify-center mx-2 py-7"
    color="#F6F6F6"
    elevation="0"
    flat
    v-if="showActionBar"
  >
    <v-card-actions>
      <v-btn
        class="text-none px-3"
        variant="tonal"
        prepend-icon="mdi-arrow-left"
        @click="navigateToProject"
      >
        Back to project
      </v-btn>
      <v-btn
        class="text-none px-3"
        variant="tonal"
        prepend-icon="mdi-text-box-check-outline"
        @click="navigateToInput"
      >
        View input
      </v-btn>
    </v-card-actions>
    <ViewingOptions
      class="text-none px-3"
      iconColor="primary"
      inactiveIcon="mdi-eye-arrow-right-outline"
      activeIcon="mdi-eye-check-outline"
      activeText="active filters"
      inactiveText="Viewing options"
      :currentScenarioId="currentItem?.id ?? 0"
      :currentProjectId="currentItem?.project ?? 0"
      @update-selected-calculations="handleSelectedCalculationsUpdate"
      @update-viewing-option="handleViewingOptionUpdate"
    />
    <DropdownMenu
      class="text-none px-3"
      iconColor="primary"
      inactiveIcon="mdi-export"
      activeIcon="mdi-export"
      activeText="active filters"
      inactiveText="Export as..."
    >
      <v-card
        class="d-flex flex-column pa-2"
        elevation="2"
        style="background: #fff; border-radius: 8px; min-width: 220px"
      >
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" size="20" class="mr-2">mdi-file-export-outline</v-icon>
          <div class="flex-grow-1">
            <span style="font-weight: 400; font-size: 1rem">Shape File</span>
          </div>
          <v-btn
            variant="text"
            color="primary"
            class="ml-2"
            style="
              font-weight: 500;
              font-size: 0.95rem;
              letter-spacing: 0.5px;
              min-width: 0;
              padding: 0 10px;
              text-transform: none;
            "
            @click="exportShapeFile"
          >
            Export
          </v-btn>
        </div>
        <div class="d-flex align-center">
          <v-icon color="primary" size="20" class="mr-2">mdi-file-export-outline</v-icon>
          <div class="flex-grow-1">
            <span style="font-weight: 400; font-size: 1rem">CSV File</span>
          </div>
          <v-btn
            variant="text"
            color="primary"
            class="ml-2"
            style="
              font-weight: 500;
              font-size: 0.95rem;
              letter-spacing: 0.5px;
              min-width: 0;
              padding: 0 10px;
              text-transform: none;
            "
            @click="exportCSVFile"
          >
            Export
          </v-btn>
        </div>
      </v-card>
    </DropdownMenu>
    <DropdownMenu
      class="text-none px-3"
      iconColor="primary"
      activeIcon="mdi-filter-check"
      inactiveIcon="mdi-filter-check-outline"
      activeText="active filters"
      inactiveText="Select your filters"
    >
      <FilterPanel
        @apply-filter="emit('apply-filter')"
        @clear-all-filter="emit('clear-all-filter')"
      />
    </DropdownMenu>
    <div class="text-none px-3">
      <v-btn
        variant="text"
        color="primary"
        class="pa-0 hide-action-bar-btn"
        style="min-width: 160px; text-transform: none; padding-left: 12px; padding-right: 12px"
        @click="showActionBar = false"
      >
        <v-icon start size="18">mdi-eye-off</v-icon>
        Hide Action Bar
      </v-btn>
    </div>
  </v-card>
</template>
